import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from '../../lib/motion';
import { Category } from '../../types';
import { categoryService } from '../../services/category.service';
import { CategoryCard } from '../../components/ui/CategoryCard';
import { HomeSearch } from '../../components/search/HomeSearch';
import { useViewModeStore } from '../../store/viewMode.store';
import { SEO } from '../../components/SEO';
import { SchemaOrg, generateWebSiteSchema, generateOrganizationSchema, generateLocalBusinessSchema } from '../../components/SchemaOrg';
import { ArrowRight, Shield, Users, Zap, Wrench, Calculator, BadgeCheck, HandCoins, MousePointerClick, MapPin } from 'lucide-react';
import { WhatsAppIcon } from '../../components/icons/BrandIcons';
import { buildWhatsAppUrl } from '../../utils/whatsapp';
import { localizePath, normalizeLanguage } from '../../utils/languageRouting';
import { CITY_CONTENT, KNOWN_CITY_SLUGS } from '../../data/cityContent';
import { PrivateLink } from '../../components/common/PrivateLink';

const POPULAR_CATEGORY_SLUGS = [
    'plomberie',
    'electricite',
    'platrerie-staff',
    'peinture',
    'nettoyage',
    'menuiserie',
    'climatisation',
    'demenagement'
];

const POPULAR_CATEGORY_COUNT = 8;

const STEP_IMAGES = ['step_01_post_v2', 'step_02_compare_v2', 'step_03_done_v2'];

const FAQ_KEYS = [1, 2, 3, 4] as const;

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-60px' },
    transition: { duration: 0.5 },
};

export const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { mode } = useViewModeStore();
    const language = normalizeLanguage(i18n.language);
    // Only public SEO roots are localized; app routes such as /jobs/create stay unprefixed.
    const localized = (path: string) => localizePath(path, language);
    // First render uses the prerendered catalogue when there is one (no swap after load); it is refreshed below.
    const [prerenderedCategories] = useState(() => categoryService.peekAllCategories());
    const [categories, setCategories] = useState<Category[]>(prerenderedCategories ?? []);
    const [isLoading, setIsLoading] = useState(!prerenderedCategories);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await categoryService.getAllCategories();
                setCategories(data ?? []);
            } catch (error) {
                console.error('Failed to load categories:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadCategories();
    }, []);

    // Popular slugs first, then fill with other categories so the grid never has holes
    // when a slug is missing from the catalogue.
    const popularCategories = [
        ...POPULAR_CATEGORY_SLUGS
            .map((slug) => categories.find((category) => category.slug === slug))
            .filter((category): category is Category => Boolean(category)),
        ...categories.filter((category) => !POPULAR_CATEGORY_SLUGS.includes(category.slug)),
    ].slice(0, POPULAR_CATEGORY_COUNT);

    const trustItems = [
        { icon: HandCoins, label: t('home.trustFree') },
        { icon: BadgeCheck, label: t('home.trustVerified') },
        { icon: MousePointerClick, label: t('home.trustChoose') },
        { icon: WhatsAppIcon, label: t('home.trustSupport') },
    ];

    const steps = [
        { title: t('home.postYourNeed'), desc: t('home.postNeedDesc'), img: STEP_IMAGES[0], alt: t('home.stepImageAlt1') },
        { title: t('home.compareOffers'), desc: t('home.compareOffersDesc'), img: STEP_IMAGES[1], alt: t('home.stepImageAlt2') },
        { title: t('home.smileItsDone'), desc: t('home.smileDesc'), img: STEP_IMAGES[2], alt: t('home.stepImageAlt3') },
    ];

    const faqs = FAQ_KEYS.map((i) => ({ q: t(`home.faqQuestion${i}`), a: t(`home.faqAnswer${i}`) }));

    return (
        <div className="relative font-sans text-foreground bg-background overflow-hidden selection:bg-primary/20">
            <SEO
                title={t('seo.homeTitle')}
                description={t('seo.homeDescription')}
                canonical="/"
            />
            <SchemaOrg schema={[
                generateWebSiteSchema(),
                generateOrganizationSchema(),
                generateLocalBusinessSchema(),
            ]} />

            {/* Hero */}
            <section className="relative z-10 min-h-[88vh] md:min-h-[90vh] flex items-center pt-20 pb-12 md:py-0">
                <div className="absolute inset-0 z-0 overflow-hidden">
                    <picture className="block w-full h-full">
                        <source media="(max-width: 767px)" srcSet="/images/hero_home_v2-mobile.webp" type="image/webp" />
                        <source srcSet="/images/hero_home_v2-desktop.webp" type="image/webp" />
                        <img
                            src="/images/hero_home_v2-desktop.jpg"
                            alt={t('home.heroImageAlt')}
                            width="1536"
                            height="1024"
                            className="w-full h-full object-cover object-[70%_center] md:object-center rtl:-scale-x-100"
                            fetchPriority="high"
                        />
                    </picture>
                    {/* Photo is mirrored in RTL so the copy never covers the tradesperson. Readability overlays: top-down on mobile (subject sits low), left-to-right on desktop (subject sits right). */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/45 to-black/10 md:hidden" />
                    <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-black/75 via-black/45 to-transparent rtl:bg-gradient-to-l" />
                    {/* Soft fade into the page so the photo edge (and its generated details) never ends on a hard line. */}
                    <div className="absolute inset-x-0 bottom-0 h-40 md:h-56 bg-gradient-to-t from-background via-background/40 to-transparent" />
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="max-w-3xl text-white text-center md:text-start mx-auto md:mx-0"
                    >
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 text-xs md:text-sm font-medium mb-5 md:mb-6 text-white/90">
                            <MapPin className="w-3.5 h-3.5 text-amber-400" />
                            {t('home.availableInMorocco')}
                        </span>

                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 tracking-tight leading-[1.05] max-w-2xl mx-auto md:mx-0">
                            {t('home.heroTitle')}{' '}
                            <span className="text-amber-400">{t('home.heroTitleHighlight')}</span>
                        </h1>

                        <p className="text-base sm:text-lg md:text-xl text-white/85 mb-7 md:mb-8 leading-relaxed max-w-2xl mx-auto md:mx-0">
                            {t('home.heroSubtitle')}
                        </p>

                        {mode !== 'provider' && (
                            <button
                                onClick={() => navigate('/jobs/create')}
                                className="inline-flex items-center gap-3 px-6 py-3.5 bg-primary text-primary-foreground font-bold rounded-full hover:bg-primary/90 transition-all shadow-xl text-base md:text-lg"
                                data-track="hero_post_request"
                            >
                                {t('home.heroCta')}
                                <ArrowRight className="w-5 h-5 rtl:rotate-180" />
                            </button>
                        )}

                        <div className="mt-6 md:mt-8 text-start text-foreground">
                            <HomeSearch />
                        </div>

                        {mode !== 'provider' && (
                            <Link
                                to={localized('/devenir-prestataire')}
                                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-amber-300 transition-colors"
                                data-track="hero_provider_cta"
                            >
                                <Wrench className="w-4 h-4" />
                                {t('home.providerBandTitle')} {t('home.becomeProvider')}
                            </Link>
                        )}
                    </motion.div>
                </div>
            </section>

            {/* Trust strip — only promises Bricolat controls, no figures */}
            <section className="border-b border-border/60 bg-background">
                <div className="container mx-auto px-4 py-6 md:py-8">
                    <ul className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                        {trustItems.map(({ icon: Icon, label }) => (
                            <li key={label} className="flex items-center gap-3 text-sm md:text-base font-medium text-foreground/80">
                                <span className="inline-flex w-10 h-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <Icon className="w-5 h-5" />
                                </span>
                                {label}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* How it works */}
            <section className="py-20 md:py-28 bg-background">
                <div className="container mx-auto px-4">
                    <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">{t('home.howItWorksTitle')}</h2>
                        <p className="text-lg md:text-xl text-muted-foreground">{t('home.howItWorksDesc')}</p>
                    </motion.div>

                    <ol className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10">
                        {steps.map((step, index) => (
                            <motion.li
                                key={step.img}
                                {...fadeUp}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="flex flex-col"
                            >
                                <div className="relative rounded-3xl overflow-hidden shadow-lg aspect-[4/3] mb-6">
                                    <picture>
                                        <source srcSet={`/images/${step.img}.webp`} type="image/webp" />
                                        <img
                                            src={`/images/${step.img}.jpg`}
                                            alt={step.alt}
                                            width="1200"
                                            height="900"
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </picture>
                                    <span className="absolute top-4 start-4 inline-flex w-10 h-10 items-center justify-center rounded-full bg-white text-primary font-bold shadow-md">
                                        {index + 1}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-bold mb-2">{step.title}</h3>
                                <p className="text-muted-foreground leading-relaxed">{step.desc}</p>
                            </motion.li>
                        ))}
                    </ol>

                    {mode !== 'provider' && (
                        <div className="mt-12 text-center">
                            <button
                                onClick={() => navigate('/jobs/create')}
                                className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-all shadow-md"
                                data-track="how_it_works_post_request"
                            >
                                {t('home.heroCta')}
                                <ArrowRight className="w-5 h-5 rtl:rotate-180" />
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Services */}
            <section className="py-20 md:py-24 bg-muted/30">
                <div className="container mx-auto px-4">
                    <motion.div
                        {...fadeUp}
                        className="flex flex-col md:flex-row justify-between md:items-end mb-10 md:mb-12 gap-4"
                    >
                        <div>
                            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-3">{t('home.ourServices')}</h2>
                            <p className="text-lg md:text-xl text-muted-foreground max-w-lg">{t('home.subtitle')}</p>
                        </div>
                        <PrivateLink
                            to="/search"
                            className="group inline-flex items-center gap-2 text-primary font-semibold text-lg hover:opacity-80 transition-opacity"
                        >
                            {t('home.viewCatalog')}
                            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
                        </PrivateLink>
                    </motion.div>

                    {isLoading ? (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                            {Array.from({ length: POPULAR_CATEGORY_COUNT }, (_, i) => (
                                <div key={i} className="h-36 bg-muted/60 rounded-xl animate-pulse" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                            {popularCategories.map((category) => (
                                <CategoryCard
                                    key={category.id}
                                    category={category}
                                    onClick={() => navigate(`/search?categoryId=${category.id}`)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Price simulator */}
            <section className="py-16 md:py-20 bg-background">
                <div className="container mx-auto px-4">
                    <motion.div
                        {...fadeUp}
                        className="relative overflow-hidden rounded-[2rem] border border-border/50 bg-gradient-to-br from-primary/5 via-background to-amber-50 dark:to-amber-950/10 p-6 sm:p-8 md:p-12"
                    >
                        <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
                            <div className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                                <Calculator className="w-8 h-8 md:w-10 md:h-10" />
                            </div>
                            <div className="flex-1 text-center md:text-start">
                                <h2 className="text-2xl md:text-3xl font-bold mb-2 tracking-tight">
                                    {t('priceSimulator.promoTitle')}
                                </h2>
                                <p className="text-muted-foreground text-base md:text-lg">
                                    {t('priceSimulator.promoSubtitle')}
                                </p>
                            </div>
                            <Link
                                to={localized('/simulateur-prix')}
                                className="w-full md:w-auto shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-all shadow-md whitespace-nowrap"
                                data-track="home_price_simulator_cta"
                            >
                                <Zap className="w-5 h-5" />
                                {t('priceSimulator.promoCta')}
                            </Link>
                        </div>
                        <div className="absolute -right-10 -top-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl rtl:right-auto rtl:-left-10" />
                    </motion.div>
                </div>
            </section>

            {/* Why Bricolat — four promises that are true today */}
            <section className="py-20 md:py-24 bg-muted/30">
                <div className="container mx-auto px-4">
                    <motion.div {...fadeUp} className="text-center mb-12 md:mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">{t('home.whyChooseUs')}</h2>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                            {t('home.whyChooseUsHonestDesc')}
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                        <motion.div {...fadeUp} className="bg-card rounded-[2rem] p-8 md:p-10 border border-border/50 shadow-sm">
                            <div className="w-12 h-12 rounded-2xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center mb-6">
                                <Shield className="w-6 h-6" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3">{t('home.rigorousVerification')}</h3>
                            <p className="text-muted-foreground leading-relaxed">{t('home.rigorousVerificationDesc')}</p>
                        </motion.div>

                        <motion.div {...fadeUp} className="bg-gradient-to-br from-primary to-blue-700 rounded-[2rem] p-8 md:p-10 shadow-xl text-primary-foreground">
                            <div className="w-12 h-12 rounded-2xl bg-white/15 text-yellow-300 flex items-center justify-center mb-6">
                                <Users className="w-6 h-6" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3">{t('home.freeQuotesTitle')}</h3>
                            <p className="text-primary-foreground/85 leading-relaxed mb-3">{t('home.freeQuotesDesc')}</p>
                            <p className="text-sm text-primary-foreground/65">{t('home.freeQuotesNote')}</p>
                        </motion.div>

                        <motion.div {...fadeUp} className="bg-card rounded-[2rem] p-8 md:p-10 border border-border/50 shadow-sm">
                            <div className="w-12 h-12 rounded-2xl bg-amber-100 dark:bg-amber-900/30 text-amber-600 flex items-center justify-center mb-6">
                                <MousePointerClick className="w-6 h-6" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3">{t('home.youChooseTitle')}</h3>
                            <p className="text-muted-foreground leading-relaxed">{t('home.youChooseDesc')}</p>
                        </motion.div>

                        <motion.div {...fadeUp} className="bg-card rounded-[2rem] p-8 md:p-10 border border-border/50 shadow-sm">
                            <div className="w-12 h-12 rounded-2xl bg-green-100 dark:bg-green-900/30 text-green-600 flex items-center justify-center mb-6">
                                <WhatsAppIcon className="w-6 h-6" title="" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3">{t('home.supportTitle')}</h3>
                            <p className="text-muted-foreground leading-relaxed mb-6">{t('home.supportDesc')}</p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link
                                    to={localized('/contact')}
                                    className="text-primary font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all"
                                >
                                    {t('home.contactSupport')} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
                                </Link>
                                <a
                                    href={buildWhatsAppUrl(t('whatsapp.messages.support'))}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-700 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all"
                                >
                                    {t('whatsapp.open')} <WhatsAppIcon className="w-4 h-4" title="" />
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Cities — internal links to the city landing pages */}
            <section className="py-20 md:py-24 bg-background">
                <div className="container mx-auto px-4">
                    <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">{t('home.citiesTitle')}</h2>
                        <p className="text-lg md:text-xl text-muted-foreground">{t('home.citiesDesc')}</p>
                    </motion.div>
                    <ul className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
                        {KNOWN_CITY_SLUGS.map((slug) => (
                            <li key={slug}>
                                <Link
                                    to={localized(`/pro/${slug}`)}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border bg-card font-medium hover:border-primary hover:text-primary transition-colors"
                                >
                                    <MapPin className="w-4 h-4" />
                                    {CITY_CONTENT[slug].name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Provider band */}
            <section className="py-16 md:py-20 bg-muted/30">
                <div className="container mx-auto px-4">
                    <motion.div
                        {...fadeUp}
                        className="rounded-[2rem] bg-foreground text-background p-8 md:p-12 flex flex-col md:flex-row items-center gap-6 md:gap-10"
                    >
                        <div className="shrink-0 w-16 h-16 rounded-2xl bg-amber-400/15 text-amber-400 flex items-center justify-center">
                            <Wrench className="w-8 h-8" />
                        </div>
                        <div className="flex-1 text-center md:text-start">
                            <h2 className="text-2xl md:text-3xl font-bold mb-2">{t('home.providerBandTitle')}</h2>
                            <p className="text-background/70 text-base md:text-lg">{t('home.providerBandDesc')}</p>
                        </div>
                        <Link
                            to={localized('/devenir-prestataire')}
                            className="w-full md:w-auto shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-amber-400 text-gray-900 font-bold rounded-xl hover:bg-amber-300 transition-colors whitespace-nowrap"
                            data-track="home_provider_band_cta"
                        >
                            {t('home.becomeProvider')}
                            <ArrowRight className="w-5 h-5 rtl:rotate-180" />
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* FAQPage JSON-LD Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqs.map(({ q, a }) => ({
                            "@type": "Question",
                            "name": q,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": a
                            }
                        }))
                    })
                }}
            />

            {/* FAQ */}
            <section className="py-20 md:py-24 bg-background">
                <div className="container mx-auto px-4 max-w-3xl">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">{t('home.faq')}</h2>
                        <p className="text-lg md:text-xl text-muted-foreground">{t('home.faqDesc')}</p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq) => (
                            <details key={faq.q} className="group bg-muted/30 rounded-2xl p-6 [&_summary::-webkit-details-marker]:hidden open:bg-muted/50 transition-colors">
                                <summary className="flex cursor-pointer items-center justify-between gap-1.5 font-bold text-lg text-foreground">
                                    <h3 className="text-lg md:text-xl">{faq.q}</h3>
                                    <span className="shrink-0 rounded-full bg-white p-1.5 text-gray-900 sm:p-3 shadow-sm group-open:bg-primary group-open:text-white transition-colors">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="size-5 shrink-0 transition duration-300 group-open:-rotate-45" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                        </svg>
                                    </span>
                                </summary>
                                <p className="mt-4 leading-relaxed text-muted-foreground">{faq.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 md:py-24 bg-foreground text-background relative overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-500 via-transparent to-transparent" />
                </div>

                <div className="container mx-auto px-4 relative z-10 text-center">
                    <motion.div {...fadeUp}>
                        <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tighter">{t('home.readyToStart')}</h2>
                        <p className="text-xl md:text-2xl text-background/70 mb-10 max-w-2xl mx-auto">
                            {t('home.finalCtaDesc')}
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4 md:gap-6">
                            {mode !== 'provider' && (
                                <button
                                    onClick={() => navigate('/jobs/create')}
                                    className="px-8 py-4 bg-primary text-primary-foreground font-bold rounded-2xl hover:bg-primary/90 transition-all text-lg md:text-xl shadow-[0_0_40px_-10px_rgba(33,84,255,0.5)]"
                                    data-track="final_post_request"
                                >
                                    {t('home.heroCta')}
                                </button>
                            )}
                            <PrivateLink
                                to="/search"
                                className="px-8 py-4 bg-background/10 backdrop-blur-md text-background font-bold rounded-2xl hover:bg-background/20 transition-all text-lg md:text-xl border border-background/20"
                            >
                                {t('home.findProvider')}
                            </PrivateLink>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};
